import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { sendOnboardingQuiz, sendFunnelEvent, sendProfile, getOnboardingProfile, postOnboardingConsent, getPlansConfig, redeemPromoCode, requestTour } from '../api/client';
import {
  Hammer, Clipboard, Building, TrendingUp, Receipt, Handshake, Scale,
  Home, Shield, MessageCircle, BookOpen, Star, Check,
  Users, Wrench, Link, IconBadge,
} from './icons';

// TODO: ссылка на пример электронной визитки — Alexandr пришлёт.
// Пока пусто — кнопка «Посмотреть пример визитки» показывает alert «скоро».
const TODO_CARD_EXAMPLE_URL = '';

// Справочник крупных городов Германии для автоподсказки поля «регион» (единое написание).
// Ввод свободный — подсказки-чипы по мере ввода (datalist убран: в TG WebView вёл себя как список).
// Ввод остаётся свободным — это лишь подсказки (datalist), не ограничение.
const DE_CITIES = [
  'Berlin', 'Hamburg', 'München', 'Köln', 'Frankfurt am Main', 'Stuttgart', 'Düsseldorf',
  'Leipzig', 'Dortmund', 'Essen', 'Bremen', 'Dresden', 'Hannover', 'Nürnberg', 'Duisburg',
  'Bochum', 'Wuppertal', 'Bielefeld', 'Bonn', 'Münster', 'Mannheim', 'Karlsruhe', 'Augsburg',
  'Wiesbaden', 'Mönchengladbach', 'Gelsenkirchen', 'Aachen', 'Braunschweig', 'Chemnitz', 'Kiel',
  'Halle (Saale)', 'Magdeburg', 'Freiburg im Breisgau', 'Krefeld', 'Mainz', 'Lübeck', 'Erfurt',
  'Oberhausen', 'Rostock', 'Kassel', 'Hagen', 'Saarbrücken', 'Potsdam', 'Ludwigshafen',
  'Oldenburg', 'Leverkusen', 'Osnabrück', 'Heidelberg', 'Darmstadt', 'Würzburg', 'Regensburg',
  'Ingolstadt', 'Wolfsburg', 'Ulm', 'Pforzheim', 'Göttingen', 'Bottrop', 'Reutlingen', 'Koblenz',
];
// Сравнение без диакритики: «mun» найдёт München, «dus» — Düsseldorf.
const foldCity = (s) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
// Подсказки к полю «регион»: города по началу ввода (от 2 символов), максимум 6 чипов.
function citySuggestions(input) {
  const q = foldCity(input.trim());
  if (q.length < 2) return [];
  return DE_CITIES.filter((c) => foldCity(c).startsWith(q) && foldCity(c) !== q).slice(0, 6);
}

// ---- Slide definitions ----
// v5: анкета (имя/телефон/город) убрана. Квиз = 6 шагов: сегмент, приоритет(мульти),
// команда, сайт, реклама(мульти), регион. Профилирующие ответы идут в users.
const SLIDES_V2 = [
  { id: 'offer',   content: 'offer' },
  { id: 'consent', content: 'consent' },
  { id: 'quiz',    content: 'quiz' },
  { id: 'segment', content: 'segment' },
  { id: 'how',     content: 'how' },
  { id: 'fork',    content: 'fork' },     // развилка: видео / экскурсия
  { id: 'form',    content: 'form' },     // анкета (обяз.)
  { id: 'present', content: 'present' },  // видео ИЛИ экскурсия + гейт-чекбокс
  { id: 'plans',   content: 'plans' },
];

// Сколько «экранов» занимает квиз для превью-нумерации (6 вопросов)
const QUIZ_SCREENS = 6;

const SLIDES_LEGACY = [
  { id: 'video',     content: 'video' },
  { id: 'excursion', content: 'excursion' },
  { id: 'plans',     content: 'plans' },
];

// Самозапись через Calendly для клиентов ВЫКЛЮЧЕНА (реш. владельца 23.09.2026): вместо календаря
// клиент оставляет заявку, менеджер сам подбирает время (Calendly остаётся только в админке).
// Чтобы вернуть самозапись — TOUR_SELF_BOOKING=true и CALENDLY_URL.
const TOUR_SELF_BOOKING = false;
const CALENDLY_URL = 'https://calendly.com/fix-hub-info/fix-hub';

// Плавно поднимает поле ввода над экранной клавиатурой — умеренно, по центру видимой области.
// Вешать на onFocus любого input в воронке. Работает вместе со scrollPaddingBottom контейнера слайда.
function liftField(e) {
  const el = e.target;
  setTimeout(() => { try { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (err) { /* noop */ } }, 350);
}

// ============================================================================
// 🧪 ПЕСОЧНИЦА (копия Onboarding.js от 06.07.2026) — новая послеоплатная воронка.
// Показывается ТОЛЬКО аккаунтам из админ-настройки «Тестовая воронка»
// (stats key onboarding_dev_ids). Прод-воронка (Onboarding.js) НЕ затронута.
// Здесь разрабатываем изменения; после приёмки владельцем переносим в прод.
// ============================================================================
// 🎬 Демо-режим (deep-link t.me/<bot>?startapp=demo, только role=admin): воронка показывается
// поверх аппки для показа клиенту. Ничего не пишем в бэкенд (события/квиз/согласие/анкета),
// промокод DEMO_PROMO_CODE «активируется» локально и возвращает в аппку.
export const DEMO_PROMO_CODE = 'TEST';

export default function Onboarding({ onComplete, onbV2, replay = false, demo = false }) {
  const slides = onbV2 ? SLIDES_V2 : SLIDES_LEGACY;
  const [current, setCurrent] = useState(0);
  const [offset, setOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [animKey, setAnimKey] = useState(0);
  const containerRef = useRef(null);
  const startX = useRef(0);
  const startTime = useRef(0);
  const dragging = useRef(false);

  // Quiz state lifted to parent so bottom-nav "Back" can control quiz steps
  const [quizStep, setQuizStep] = useState(0);
  const [quizSegment, setQuizSegment] = useState(null);
  const [quizPriority, setQuizPriority] = useState([]); // мультивыбор
  const [quizTeam, setQuizTeam] = useState(null);       // v5: размер команды
  const [quizSite, setQuizSite] = useState(null);       // v5: есть ли сайт
  const [quizMarketing, setQuizMarketing] = useState([]); // v5: реклама (мультивыбор)

  // Блок «развилка → оплата» (v5.1)
  const [forkPath, setForkPath] = useState(null); // 'video' | 'excursion'
  const [kb, setKb] = useState(null); // визивпорт клавиатуры: { h, top } — оверлей сжимается под неё
  const [contact, setContact] = useState({ firstName: '', lastName: '', phone: '', email: '', shared: false });

  // Возвратный пользователь (уже проходил квиз) + пропуск контента
  const [returning, setReturning] = useState(false);
  const [skipContent, setSkipContent] = useState(false);
  const [alreadyConsented, setAlreadyConsented] = useState(false);

  const sentEvents = useRef({});

  const fireEvent = useCallback((step, meta) => {
    if (sentEvents.current[step]) return;
    sentEvents.current[step] = true;
    if (!demo) sendFunnelEvent(step, meta);
  }, [demo]);

  // Find slide index by id (safe — never hardcode numbers)
  const idxOf = useCallback((id) => slides.findIndex(s => s.id === id), [slides]);

  const goTo = useCallback((indexOrId, animate = true) => {
    const index = typeof indexOrId === 'string' ? slides.findIndex(s => s.id === indexOrId) : indexOrId;
    const clamped = Math.max(0, Math.min(index, slides.length - 1));
    setCurrent(clamped);
    if (animate) setAnimKey(k => k + 1);
    setOffset(0);
    // Reset quiz step when navigating to quiz slide
    if (slides[clamped]?.content === 'quiz') {
      // keep quizStep as-is so back works; caller sets it
    }
  }, [slides]);

  // Fire per-slide analytics events
  useEffect(() => {
    const slide = slides[current];
    if (!slide) return;
    const eventMap = {
      video:     'onb_video',
      excursion: 'onb_excursion',
      offer:     'onb_offer',
      consent:   'onb_consent',
      quiz:      'onb_quiz_start',
      segment:   'onb_segment_view',
      how:       'onb_how',
      fork:      'onb_fork',
      form:      'onb_form',
      present:   'onb_present',
      plans:     'onb_plans',
    };
    const ev = eventMap[slide.content];
    if (ev) fireEvent(ev);
    // Презентация v2 — ОДИН слайд на оба пути (видео/экскурсия). В legacy это были
    // отдельные слайды video/excursion со своими событиями; после перехода на v2 (16.06)
    // onb_video/onb_excursion перестали писаться — аналитика «видео vs экскурсия» ослепла.
    // Восстанавливаем сигнал: помечаем, какой режим показан (forkPath выбран на развилке).
    if (slide.content === 'present') {
      fireEvent(forkPath === 'excursion' ? 'onb_excursion' : 'onb_video');
    }
  }, [current, slides, fireEvent, forkPath]);

  // Детект возвратного + префилл при маунте (только для v2-воронки)
  useEffect(() => {
    if (!onbV2) return;
    if (demo) {
      // Демо: без запроса профиля (иначе пропустится согласие и прыгнет на развилку) — только префилл из Telegram
      const tgUser = window.Telegram?.WebApp?.initDataUnsafe?.user;
      setContact(c => ({ ...c, firstName: tgUser?.first_name || c.firstName, lastName: tgUser?.last_name || c.lastName }));
      return;
    }
    let cancelled = false;
    getOnboardingProfile().then((p) => {
      if (cancelled) return;
      // Префилл контактов из БД; fallback на Telegram если БД пусто
      const tgUser = window.Telegram?.WebApp?.initDataUnsafe?.user;
      setContact(c => ({
        ...c,
        firstName: p.first_name || tgUser?.first_name || c.firstName,
        lastName: p.last_name || tgUser?.last_name || c.lastName,
        phone: (p.phone && p.phone !== 'shared_via_telegram') ? p.phone : c.phone,
        email: p.email || c.email,
      }));
      // Согласие: уже давал GDPR-consent → пропускаем экран consent
      if (p.gdpr_consent) setAlreadyConsented(true);
      // Возвратный: уже заполнен сегмент → пропрыгнуть на развилку.
      // В режиме replay («ознакомиться с начала» с Paywall) НЕ прыгаем — проходим воронку с offer.
      if (p.segment && !replay) {
        setReturning(true);
        setForkPath(p.funnel_path || 'video');
        // Прыгаем только если ещё на первом слайде (не сбиваем ручную навигацию)
        const forkIdx = slides.findIndex(s => s.id === 'fork');
        setCurrent(cur => (cur === 0 ? forkIdx : cur));
        setAnimKey(k => k + 1);
      }
    }).catch(() => { /* не ломаем воронку при ошибке */ });
    return () => { cancelled = true; };
  }, [onbV2]); // eslint-disable-line react-hooks/exhaustive-deps

  // Swipe handlers
  const handleTouchStart = (e) => {
    startX.current = e.touches[0].clientX;
    startTime.current = Date.now();
    dragging.current = true;
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!dragging.current) return;
    const dx = e.touches[0].clientX - startX.current;
    if (!onbV2) {
      // Legacy: свайп в обе стороны
      setOffset(dx);
    } else {
      // v2: блокируем свайп вперёд (отрицательный dx);
      // на экране оплаты полностью гасим свайп — чтобы случайно не ушли назад с оплаты
      if (dx < 0 || slides[current]?.content === 'plans') { setOffset(0); return; }
      setOffset(dx);
    }
  };

  const handleTouchEnd = () => {
    if (!dragging.current) return;
    dragging.current = false;
    setIsDragging(false);
    const elapsed = Date.now() - startTime.current;
    const velocity = Math.abs(offset) / elapsed;

    if (!onbV2) {
      // Legacy: свайп в любую сторону переключает слайды
      if ((offset > 60 || velocity > 0.4) && current > 0) {
        goTo(current - 1);
      } else if ((offset < -60 || velocity > 0.4) && offset < 0 && current < slides.length - 1) {
        goTo(current + 1);
      } else {
        setOffset(0);
      }
    } else {
      // v2: свайп назад; на квизе учитываем quizStep; на оплате свайп-назад отключён
      const onPlans = slides[current]?.content === 'plans';
      if (!onPlans && (offset > 60 || velocity > 0.4)) {
        const slide = slides[current];
        if (slide?.content === 'quiz' && quizStep > 0) {
          setQuizStep(s => s - 1);
          setOffset(0);
        } else if (current > 0) {
          goTo(current - 1);
        } else {
          setOffset(0);
        }
      } else {
        setOffset(0);
      }
    }
  };

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Клавиатура: сжимаем оверлей ровно под видимую область (visualViewport), чтобы поле ввода
  // всегда оставалось над клавиатурой без больших отступов и «прыжков». Если API нет — просто
  // ничего не делаем (fallback на прежнее поведение, без регресса).
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      const hidden = window.innerHeight - vv.height > 120; // клавиатура открыта, если ужались >120px
      setKb(hidden ? { h: vv.height, top: vv.offsetTop } : null);
    };
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    update();
    return () => { vv.removeEventListener('resize', update); vv.removeEventListener('scroll', update); };
  }, []);

  // Handle unified "← Назад" from bottom panel
  const handleBack = () => {
    const slide = slides[current];
    if (slide?.content === 'quiz' && quizStep > 0) {
      setQuizStep(s => s - 1);
    } else if (current > 0) {
      goTo(current - 1);
    }
  };

  // Quiz completion callback (v5: единый payload; segment/priority уже в state по шагам)
  const handleQuizDone = useCallback((payload) => {
    if (!demo) sendOnboardingQuiz(payload).catch(() => {});
    fireEvent('onb_quiz_done');
    goTo(current + 1);
  }, [current, goTo, fireEvent]);

  // Skip: v2 → jump to plans; legacy → onComplete (как в проде)
  const handleSkip = () => {
    if (!onbV2) {
      onComplete();
      return;
    }
    const plansIdx = idxOf('plans');
    if (plansIdx >= 0) goTo(plansIdx);
    else onComplete();
  };

  const isPlansSlide = slides[current]?.content === 'plans';
  const showBack = current > 0;

  // Логическая нумерация экранов для превью-метки (согласована с владельцем):
  // квиз — это QUIZ_SCREENS отдельных экранов (В1–В2), поэтому всего = слайдов + (QUIZ_SCREENS-1)
  const quizIdx = slides.findIndex(s => s.content === 'quiz');
  const totalScreens = slides.length + (quizIdx >= 0 ? QUIZ_SCREENS - 1 : 0);
  const screenNum = quizIdx < 0 || current < quizIdx
    ? current + 1
    : current === quizIdx
      ? quizIdx + 1 + quizStep
      : current + QUIZ_SCREENS;

  const translateX = -(current * 100) + (offset / (containerRef.current?.offsetWidth || 375)) * 100;

  return (
    <div className="onb-root" style={{
      position: 'fixed', left: 0, right: 0, zIndex: 10000,
      top: kb ? kb.top : 0,
      height: kb ? kb.h : '100%',
      background: 'var(--bg-primary)',
      display: 'flex', flexDirection: 'column',
      paddingTop: 'var(--tg-safe-top, 0px)',
      paddingBottom: kb ? 0 : 'var(--tg-safe-bottom, 0px)',
      transition: 'height .18s ease',
    }}>
      {/* Бейдж песочницы + номер экрана — чтобы ссылаться «на экране N поменяй…» */}
      <div style={{
        position: 'absolute', top: 'calc(var(--tg-safe-top, 0px) + 18px)', right: 10, zIndex: 10001,
        background: '#FFD43B', color: '#111', fontWeight: 700, fontSize: 11,
        padding: '3px 9px', borderRadius: 8, letterSpacing: '0.3px',
      }}>🧪 №{screenNum}/{totalScreens} · {slides[current]?.id}{slides[current]?.content === 'quiz' ? '-' + (quizStep + 1) : ''}</div>
      {/* Progress bar — одна плашка на каждый реальный экран (квиз = QUIZ_SCREENS шагов) */}
      <div style={{ padding: '12px 16px 0', flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 5 }}>
          {Array.from({ length: totalScreens }).map((_, i) => (
            <div key={i} style={{
              flex: 1, height: 3, borderRadius: 2,
              background: 'var(--bg-elevated)', overflow: 'hidden',
            }}>
              <div style={{
                height: '100%', borderRadius: 2,
                background: 'var(--accent)',
                width: i < screenNum - 1 ? '100%' : i === screenNum - 1 ? '45%' : '0%',
                transition: 'width 0.4s ease',
              }} />
            </div>
          ))}
        </div>
      </div>

      {/* Skip button */}
      <div style={{
        display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
        padding: '8px 16px 0', flexShrink: 0,
      }}>
        {/* v2: «Пропустить» убран — путь к тарифам только через анкету + чекбокс «ознакомился» */}
        {!onbV2 && !isPlansSlide ? (
          <div onClick={handleSkip} style={{
            color: 'var(--text-tertiary)', fontSize: 13,
            cursor: 'pointer', padding: '6px 12px',
            background: 'var(--bg-elevated)', borderRadius: 10,
          }}>
            Пропустить
          </div>
        ) : (
          <div style={{ padding: '6px 12px', fontSize: 13, opacity: 0 }}>—</div>
        )}
      </div>

      {/* Slides */}
      <div
        ref={containerRef}
        style={{ flex: 1, overflow: 'hidden', position: 'relative' }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div style={{
          display: 'flex', height: '100%',
          transform: `translateX(${translateX}%)`,
          transition: isDragging ? 'none' : 'transform 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          willChange: 'transform',
        }}>
          {slides.map((slide, i) => (
            <div key={slide.id} style={{
              minWidth: '100%', height: '100%',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              padding: '20px 24px',
              overflow: 'auto', scrollPaddingBottom: 40,
            }}>
              {slide.content === 'offer'     && (
                <OfferSlide animKey={animKey} onNext={() => goTo(idxOf(alreadyConsented ? 'quiz' : 'consent'))} />
              )}
              {slide.content === 'consent'   && (
                <ConsentSlide animKey={animKey} demo={demo} onAccept={() => goTo(idxOf('quiz'))} />
              )}
              {slide.content === 'quiz'      && (
                <QuizSlide
                  animKey={animKey}
                  quizStep={quizStep}
                  setQuizStep={setQuizStep}
                  segment={quizSegment}
                  setSegment={setQuizSegment}
                  priority={quizPriority}
                  setPriority={setQuizPriority}
                  team={quizTeam}
                  setTeam={setQuizTeam}
                  site={quizSite}
                  setSite={setQuizSite}
                  marketing={quizMarketing}
                  setMarketing={setQuizMarketing}
                  onDone={handleQuizDone}
                />
              )}
              {slide.content === 'segment'   && (
                <SegmentSlide animKey={animKey} segment={quizSegment} onNext={() => goTo(current + 1)} />
              )}
              {slide.content === 'how'       && (
                <HowSlide animKey={animKey} onNext={() => goTo(current + 1)} />
              )}
              {slide.content === 'fork'      && (
                <ForkSlide animKey={animKey} returning={returning}
                  onPick={(p) => { setForkPath(p); setSkipContent(false); goTo(idxOf('form')); }}
                  onAlreadyKnown={() => { setSkipContent(true); goTo(idxOf('form')); }}
                />
              )}
              {slide.content === 'form'      && (
                <FormSlide
                  animKey={animKey}
                  demo={demo}
                  contact={contact}
                  setContact={setContact}
                  path={forkPath}
                  onSubmit={() => goTo(idxOf(skipContent ? 'plans' : 'present'))}
                />
              )}
              {slide.content === 'present'   && (
                <PresentSlide
                  animKey={animKey}
                  path={forkPath}
                  onCall={() => fireEvent('onb_fork_call')}
                  onContinue={() => goTo(idxOf('plans'))}
                />
              )}
              {slide.content === 'plans'     && (
                <PlansSlide
                  animKey={animKey}
                  active={i === current}
                  segment={quizSegment}
                  priority={quizPriority}
                  demo={demo}
                  onDemoDone={onComplete}
                  onPayClick={(plan, period) => fireEvent('onb_pay_click', { plan, period })}
                />
              )}
              {/* Legacy slides */}
              {slide.content === 'video'     && <VideoSlide animKey={animKey} />}
              {slide.content === 'excursion' && <ExcursionSlide animKey={animKey} />}
            </div>
          ))}
        </div>
      </div>

      {/* Bottom nav */}
      {onbV2 ? (
        /* v2: только текстовая ссылка «← Назад» */
        <div style={{ padding: '12px 24px 20px', flexShrink: 0, textAlign: 'center', minHeight: 52, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16 }}>
          {showBack && (
            <span
              onClick={handleBack}
              style={{
                cursor: 'pointer', padding: '8px 16px',
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontSize: 14, fontWeight: 600,
              }}
            >
              <span style={{ color: 'var(--accent)', fontSize: 20, fontWeight: 700, lineHeight: 1 }}>←</span>
              <span style={{ color: 'var(--text-tertiary)' }}>Назад</span>
            </span>
          )}
          {/* replay-режим (повторный просмотр с Paywall): явный выход обратно на Paywall с любого экрана */}
          {(replay || demo) && (
            <span
              onClick={onComplete}
              style={{
                cursor: 'pointer', padding: '8px 16px',
                fontSize: 14, fontWeight: 600, color: 'var(--text-tertiary)',
              }}
            >
              Закрыть ✕
            </span>
          )}
        </div>
      ) : (
        /* Legacy: классическая нижняя панель с кнопками */
        <div style={{
          padding: '12px 24px 20px', flexShrink: 0,
          display: 'flex', gap: 12, alignItems: 'center',
        }}>
          {current > 0 && (
            <button
              onClick={() => goTo(current - 1)}
              style={{
                flex: 1, padding: '14px 0',
                background: 'var(--bg-elevated)', color: 'var(--text-primary)',
                border: '1px solid var(--border)', borderRadius: 14,
                fontSize: 15, fontWeight: 600, cursor: 'pointer',
              }}
            >
              ← Назад
            </button>
          )}
          {current < slides.length - 1 ? (
            <button
              onClick={() => goTo(current + 1)}
              style={{
                flex: 2, padding: '14px 0',
                background: 'var(--accent)', color: '#0D0D0D',
                border: 'none', borderRadius: 14,
                fontSize: 15, fontWeight: 700, cursor: 'pointer',
              }}
            >
              Далее →
            </button>
          ) : (
            <button
              onClick={onComplete}
              style={{
                flex: 2, padding: '14px 0',
                background: 'var(--accent)', color: '#0D0D0D',
                border: 'none', borderRadius: 14,
                fontSize: 15, fontWeight: 700, cursor: 'pointer',
              }}
            >
              Начать
            </button>
          )}
        </div>
      )}

      <style>{`
        /* Универсальный тактильный отклик — все кнопки воронки «продавливаются» при нажатии */
        .onb-root button { -webkit-tap-highlight-color: transparent; }
        .onb-root button:not(:disabled):active { transform: scale(0.97); transition: transform .08s ease; }
        @keyframes onb-fadeUp {
          from { opacity: 0; transform: translateY(16px); filter: blur(4px); }
          to { opacity: 1; transform: translateY(0); filter: blur(0); }
        }
        @keyframes onb-fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes onb-scaleIn {
          from { opacity: 0; transform: scale(0.94); filter: blur(4px); }
          to { opacity: 1; transform: scale(1); filter: blur(0); }
        }
        /* Премиум-моторика: единая «мягкая пружина» + мягкий blur-fade, стаггер по глубине */
        .onb-anim-1 { animation: onb-fadeUp 0.6s cubic-bezier(0.22,0.68,0,1) both; animation-delay: 0.04s; }
        .onb-anim-2 { animation: onb-scaleIn 0.6s cubic-bezier(0.22,0.68,0,1) both; animation-delay: 0.12s; }
        .onb-anim-3 { animation: onb-fadeUp 0.6s cubic-bezier(0.22,0.68,0,1) both; animation-delay: 0.22s; }
        .onb-anim-4 { animation: onb-fadeUp 0.6s cubic-bezier(0.22,0.68,0,1) both; animation-delay: 0.34s; }
        .onb-quiz-btn {
          display: flex; align-items: center; width: 100%; padding: 14px 16px;
          background: var(--bg-card); color: var(--text-primary);
          border: 1.5px solid var(--border); border-radius: 14px;
          font-size: 15px; font-weight: 600; cursor: pointer;
          text-align: left; transition: border-color 0.15s, background 0.15s;
          margin-bottom: 10px;
        }
        .onb-quiz-btn:active { background: var(--bg-elevated); }
        .onb-quiz-btn.selected {
          border-color: var(--accent); background: rgba(255,204,0,0.08);
        }
        @keyframes iconPop {
          from { transform: scale(.6); opacity: 0; }
          to   { transform: scale(1);  opacity: 1; }
        }
        .icon-pop { animation: iconPop .35s cubic-bezier(.34,1.56,.64,1) both; }
        .icon-pop-1 { animation-delay: .05s; }
        .icon-pop-2 { animation-delay: .15s; }
        .icon-pop-3 { animation-delay: .25s; }
        @keyframes quizCheck {
          from { transform: scale(0); }
          to   { transform: scale(1); }
        }
        .quiz-check { animation: quizCheck .25s ease both; display: flex; align-items: center; }
        .club-tile { transition: transform .12s ease, border-color .2s ease; -webkit-tap-highlight-color: transparent; }
        .club-tile:active { transform: scale(.97); border-color: var(--accent); }
        @keyframes onb-sheet-up { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @keyframes plan-glow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(255,204,0,0.0); }
          50% { box-shadow: 0 0 14px 1px rgba(255,204,0,0.5); }
        }
        .plan-year-pulse { animation: plan-glow 1.8s ease-in-out infinite; }
        @keyframes onb-price-pop {
          from { opacity: .4; transform: translateY(-4px) scale(.96); }
          to   { opacity: 1;  transform: translateY(0) scale(1); }
        }
        .price-pop { display: inline-block; animation: onb-price-pop .28s cubic-bezier(.34,1.56,.64,1) both; }
        .plan-toggle { -webkit-tap-highlight-color: transparent; }
        .plan-toggle:active { transform: scale(.95); }
        .plan-card { -webkit-tap-highlight-color: transparent; }
        .plan-card:active { filter: brightness(1.04); }
        .plan-cta { -webkit-tap-highlight-color: transparent; transition: transform .1s ease, filter .15s ease; }
        .plan-cta:active { transform: scale(.96); filter: brightness(1.07); }
        .plan-pay-live { position: relative; overflow: hidden; }
        .plan-pay-live::after {
          content: ''; position: absolute; top: 0; left: -60%; width: 45%; height: 100%;
          background: linear-gradient(100deg, transparent, rgba(255,255,255,0.5), transparent);
          transform: skewX(-20deg); pointer-events: none;
          animation: onb-shine 2.8s ease-in-out infinite;
        }
        @keyframes onb-shine { 0% { left: -60%; } 55%, 100% { left: 130%; } }
        @keyframes onb-reveal { from { opacity: 0; transform: translateX(-6px); } to { opacity: 1; transform: translateX(0); } }
        .plan-reveal { animation: onb-reveal .38s ease both; }
        @media (prefers-reduced-motion: reduce) {
          .plan-pay-live::after, .plan-reveal, .price-pop,
          .onb-anim-1, .onb-anim-2, .onb-anim-3, .onb-anim-4 { animation: none; filter: none; }
        }
      `}</style>
    </div>
  );
}

/* ======== SLIDE COMPONENTS ======== */

/* ---- Экран 1: Оффер ---- */
// A/B заголовка: дефолт — клубное позиционирование (клуб впереди, заказы — одна из опор);
// sharp — «острый» вариант для теста, не дефолт (решение 2026-06-12)
const OFFER_HEADLINES = {
  club:  'Закрытый клуб строителей в Германии — с собственной платформой и инструментами для роста бизнеса',
  sharp: 'Закрытый клуб строителей в Германии, где заказы достаются вам, а не посреднику',
};
const OFFER_HEADLINE = OFFER_HEADLINES.club;

function OfferSlide({ animKey, onNext }) {
  return (
    <div style={{ textAlign: 'center', width: '100%', maxWidth: 400 }}>
      <div key={`o1-${animKey}`} className="onb-anim-1">
        <img src="/logo.png" alt="Fix-Hub" style={{ height: 48, marginBottom: 24, objectFit: 'contain' }} />
      </div>

      <h1 key={`o2-${animKey}`} className="onb-anim-2" style={{
        fontSize: 22, fontWeight: 800, marginBottom: 14,
        color: 'var(--text-primary)', lineHeight: 1.3,
      }}>
        {OFFER_HEADLINE}
      </h1>

      <p key={`o3-${animKey}`} className="onb-anim-3" style={{
        fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 20,
      }}>
        Сообщество, знания и инструменты — чтобы расти, а не выживать в одиночку.
      </p>

      <p key={`o4-${animKey}`} className="onb-anim-4" style={{
        fontSize: 13, color: 'var(--text-tertiary)', marginBottom: 24,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
      }}>
        <Users size={16} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
        Нам доверяют более 450 фирм
      </p>

      <div key={`o5-${animKey}`} className="onb-anim-4">
        <button onClick={onNext} style={{
          width: '100%', padding: '16px 0',
          background: 'var(--accent)', color: '#0D0D0D',
          border: 'none', borderRadius: 14, fontSize: 16, fontWeight: 700, cursor: 'pointer',
          marginBottom: 10,
        }}>
          Показать, что подойдёт именно вам →
        </button>
        <p style={{ fontSize: 12, color: 'var(--text-tertiary)', margin: 0 }}>
          5 коротких вопросов · меньше минуты
        </p>
      </div>
    </div>
  );
}

/* ---- Экран 1б: Согласие (GDPR + write_access) ---- */
function ConsentSlide({ animKey, onAccept, demo = false }) {
  const [agreed, setAgreed] = useState(false);

  const openLink = (url) => {
    const tg = window.Telegram?.WebApp;
    if (tg?.openLink) tg.openLink(url); else window.open(url, '_blank');
  };

  const handleContinue = () => {
    if (!agreed) return;
    const tg = window.Telegram?.WebApp;
    const startParam = tg?.initDataUnsafe?.start_param || '';
    const proceed = (granted) => {
      // fire-and-forget: не ждём ответ и не роняем UX; .catch гасит unhandled rejection при сбое сети
      if (!demo) { try { postOnboardingConsent({ write_access: granted ? 1 : 0, start_param: startParam }).catch(() => {}); } catch (e) {} }
      onAccept();
    };
    if (tg && typeof tg.requestWriteAccess === 'function') {
      try { tg.requestWriteAccess((granted) => proceed(!!granted)); }
      catch (e) { proceed(false); }
    } else {
      proceed(false);
    }
  };

  return (
    <div style={{ textAlign: 'center', width: '100%', maxWidth: 400 }}>
      <div key={`cs1-${animKey}`} className="onb-anim-1">
        <img src="/logo.png" alt="Fix-Hub" style={{ height: 48, marginBottom: 24, objectFit: 'contain' }} />
      </div>

      <h2 key={`cs2-${animKey}`} className="onb-anim-2" style={{
        fontSize: 22, fontWeight: 800, marginBottom: 8,
        color: 'var(--text-primary)', lineHeight: 1.3,
      }}>
        Прежде чем начнём
      </h2>

      <p key={`cs3-${animKey}`} className="onb-anim-3" style={{
        fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 24,
      }}>
        Пара формальностей — и переходим к делу.
      </p>

      <div key={`cs4-${animKey}`} className="onb-anim-4" style={{ textAlign: 'left' }}>
        <GateCheckbox
          checked={agreed}
          onToggle={() => setAgreed(a => !a)}
          label="Я соглашаюсь на обработку моих данных и получение сообщений о клубе Fix-Club"
        />

        <p style={{ fontSize: 12, color: 'var(--text-secondary)', textAlign: 'center', marginBottom: 20, lineHeight: 1.5 }}>
          Подробнее:{' '}
          <span
            onClick={() => openLink('https://fix-hub.de/datenschutz/')}
            style={{ color: 'var(--accent)', textDecoration: 'underline', cursor: 'pointer' }}
          >
            Datenschutz
          </span>
          {' · '}
          <span
            onClick={() => openLink('https://fix-hub.de/impressum/')}
            style={{ color: 'var(--accent)', textDecoration: 'underline', cursor: 'pointer' }}
          >
            Impressum
          </span>
        </p>

        <button onClick={handleContinue} disabled={!agreed} style={{
          width: '100%', padding: '16px 0',
          background: agreed ? 'var(--accent)' : 'var(--bg-elevated)',
          color: agreed ? '#0D0D0D' : 'var(--text-tertiary)',
          border: 'none', borderRadius: 14, fontSize: 16, fontWeight: 700,
          cursor: agreed ? 'pointer' : 'default', transition: 'all 0.2s',
          boxShadow: agreed ? '0 6px 20px rgba(255,204,0,0.28)' : 'none',
        }}>
          Продолжить →
        </button>
      </div>
    </div>
  );
}

/* ---- Экран 2: Квиз (состояние поднято в родителя) ---- */
// Универсальная кнопка-вариант квиза (single/multi)
function QuizOption({ Icon, label, sel, onClick }) {
  return (
    <button
      className={`onb-quiz-btn${sel ? ' selected' : ''}`}
      onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', gap: 12 }}
    >
      <IconBadge accent={sel} size={32} iconSize={18}>
        <Icon />
      </IconBadge>
      <span style={{ flex: 1, textAlign: 'left' }}>{label}</span>
      {sel && (
        <span className="quiz-check" style={{ color: 'var(--accent)', flexShrink: 0 }}>
          <Check size={18} />
        </span>
      )}
    </button>
  );
}

function QuizContinue({ enabled, onClick, label = 'Дальше →' }) {
  return (
    <button
      onClick={onClick}
      disabled={!enabled}
      style={{
        width: '100%', marginTop: 6, padding: '15px 0',
        background: enabled ? 'var(--accent)' : 'var(--bg-elevated)',
        color: enabled ? '#0D0D0D' : 'var(--text-tertiary)',
        border: 'none', borderRadius: 14, fontSize: 16, fontWeight: 700,
        cursor: enabled ? 'pointer' : 'default', transition: 'all 0.2s',
      }}
    >
      {label}
    </button>
  );
}

function QuizHead({ num, title, hint }) {
  return (
    <>
      <p className="onb-anim-1" style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 8 }}>
        Вопрос {num} из 5
      </p>
      <h2 className="onb-anim-1" style={{ fontSize: 20, fontWeight: 700, marginBottom: hint ? 4 : 18, color: 'var(--text-primary)', lineHeight: 1.3 }}>
        {title}
      </h2>
      {hint && (
        <p className="onb-anim-2" style={{ fontSize: 13, color: 'var(--text-tertiary)', marginBottom: 18 }}>
          {hint}
        </p>
      )}
    </>
  );
}

function QuizSlide({
  animKey, quizStep, setQuizStep,
  segment, setSegment, priority, setPriority,
  team, setTeam, site, setSite, marketing, setMarketing, onDone,
}) {
  const [regionInput, setRegionInput] = useState('');
  const prioSel = Array.isArray(priority) ? priority : [];
  const mktSel = Array.isArray(marketing) ? marketing : [];

  const handleSegment = (val) => { setSegment(val); setTimeout(() => setQuizStep(1), 200); };
  const handleTeam = (val) => { setTeam(val); setTimeout(() => setQuizStep(3), 200); };
  const handleSite = (val) => { setSite(val); setTimeout(() => setQuizStep(4), 200); };

  // Мультивыбор: эксклюзивный вариант (all / none) гасит остальные и наоборот
  const makeToggle = (setter, exclusive) => (val) => {
    setter(prev => {
      const cur = Array.isArray(prev) ? prev : [];
      if (val === exclusive) return cur.includes(exclusive) ? [] : [exclusive];
      const base = cur.filter(p => p !== exclusive);
      return base.includes(val) ? base.filter(p => p !== val) : [...base, val];
    });
  };
  const togglePriority = makeToggle(setPriority, 'all');
  const toggleMarketing = makeToggle(setMarketing, 'none');

  const finish = () => onDone({
    segment: segment || 'novice',
    priorities: prioSel,
    team_size: team || '',
    has_website: site || '',
    marketing: mktSel,
    region: regionInput.trim(),
  });

  return (
    <div style={{ width: '100%', maxWidth: 400 }}>
      {/* Вопрос 1 — сегмент */}
      {quizStep === 0 && (
        <div key={`q-${animKey}-0`}>
          <QuizHead num={1} title="Что лучше всего описывает вас сейчас?" hint="Без спама. Это нужно, чтобы показать вам то, что важно именно вам." />
          <div className="onb-anim-3">
            {[
              { val: 'novice',        Icon: Hammer,    label: 'Начинаю / работаю один или небольшой бригадой' },
              { val: 'subcontractor', Icon: Clipboard, label: 'Работаю на субподряде — хочу своих клиентов' },
              { val: 'firm',          Icon: Building,  label: 'У меня своя фирма — ищу надёжных людей и партнёров' },
            ].map(({ val, Icon, label }) => (
              <QuizOption key={val} Icon={Icon} label={label} sel={segment === val} onClick={() => handleSegment(val)} />
            ))}
          </div>
        </div>
      )}

      {/* Вопрос 2 — приоритет (мультивыбор) */}
      {quizStep === 1 && (
        <div key={`q-${animKey}-1`}>
          <QuizHead num={2} title="Что для вас сейчас важнее всего?" hint="Можно выбрать несколько" />
          <div className="onb-anim-3">
            {[
              { val: 'clients',     Icon: TrendingUp, label: 'Больше заказов и клиентов' },
              { val: 'routine',     Icon: Receipt,    label: 'Меньше бумажной рутины: сметы, счета, цены' },
              { val: 'contractors', Icon: Handshake,  label: 'Надёжные мастера и субподряд' },
              { val: 'taxes',       Icon: Scale,      label: 'Налоги, документы, право' },
              { val: 'all',         Icon: Check,      label: 'Всё важно' },
            ].map(({ val, Icon, label }) => (
              <QuizOption key={val} Icon={Icon} label={label} sel={prioSel.includes(val)} onClick={() => togglePriority(val)} />
            ))}
          </div>
          <QuizContinue enabled={prioSel.length > 0} onClick={() => setQuizStep(2)} />
        </div>
      )}

      {/* Вопрос 3 — размер команды */}
      {quizStep === 2 && (
        <div key={`q-${animKey}-2`}>
          <QuizHead num={3} title="Сколько вас в команде?" />
          <div className="onb-anim-3">
            {[
              { val: 'solo',   Icon: Hammer,   label: 'Работаю один' },
              { val: 'duo',    Icon: Users,    label: 'Вдвоём с напарником' },
              { val: '3-5',    Icon: Users,    label: '3–5 человек' },
              { val: '5-10',   Icon: Users,    label: '5–10 человек' },
              { val: '10plus', Icon: Building, label: 'Больше 10' },
            ].map(({ val, Icon, label }) => (
              <QuizOption key={val} Icon={Icon} label={label} sel={team === val} onClick={() => handleTeam(val)} />
            ))}
          </div>
        </div>
      )}

      {/* Вопрос 4 — сайт */}
      {quizStep === 3 && (
        <div key={`q-${animKey}-3`}>
          <QuizHead num={4} title="Есть ли у вас свой сайт?" />
          <div className="onb-anim-3">
            {[
              { val: 'no',  Icon: Wrench, label: 'Пока нет сайта' },
              { val: 'yes', Icon: Link,   label: 'Да, сайт есть' },
            ].map(({ val, Icon, label }) => (
              <QuizOption key={val} Icon={Icon} label={label} sel={site === val} onClick={() => handleSite(val)} />
            ))}
          </div>
        </div>
      )}

      {/* Вопрос 5 — реклама (мультивыбор) */}
      {quizStep === 4 && (
        <div key={`q-${animKey}-4`}>
          <QuizHead num={5} title="Как привлекаете клиентов?" hint="Можно выбрать несколько" />
          <div className="onb-anim-3">
            {[
              { val: 'social',     Icon: MessageCircle, label: 'Развиваю соцсети' },
              { val: 'fb_ads',     Icon: TrendingUp,    label: 'Платная реклама в Facebook / Instagram' },
              { val: 'google_ads', Icon: Star,          label: 'Платная реклама в Google' },
              { val: 'none',       Icon: Wrench,        label: 'Пока не вкладываюсь в рекламу' },
            ].map(({ val, Icon, label }) => (
              <QuizOption key={val} Icon={Icon} label={label} sel={mktSel.includes(val)} onClick={() => toggleMarketing(val)} />
            ))}
          </div>
          <QuizContinue enabled={mktSel.length > 0} onClick={() => setQuizStep(5)} />
        </div>
      )}

      {/* Финальный шаг — регион (без номера, чтобы «5 вопросов» сходилось) */}
      {quizStep === 5 && (
        <div key={`q-${animKey}-5`}>
          <p className="onb-anim-1" style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 8 }}>
            Последний штрих
          </p>
          <h2 className="onb-anim-1" style={{ fontSize: 20, fontWeight: 700, marginBottom: 4, color: 'var(--text-primary)', lineHeight: 1.3 }}>
            В каком регионе вы работаете?
          </h2>
          <p className="onb-anim-2" style={{ fontSize: 13, color: 'var(--text-tertiary)', marginBottom: 18 }}>
            Чтобы показывать объекты и людей ближе к вам.
          </p>
          <div className="onb-anim-3">
            <input
              type="text"
              value={regionInput}
              onChange={(e) => setRegionInput(e.target.value)}
              onFocus={liftField}
              placeholder="Город или регион (например, Berlin)"
              maxLength={100}
              autoComplete="off"
              style={{
                width: '100%', padding: '14px 16px',
                background: 'var(--bg-card)', color: 'var(--text-primary)',
                border: '1.5px solid var(--border)', borderRadius: 14,
                fontSize: 16, boxSizing: 'border-box', marginBottom: 12, outline: 'none',
              }}
            />
            {citySuggestions(regionInput).length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                {citySuggestions(regionInput).map((c) => (
                  <button key={c} type="button" onClick={() => setRegionInput(c)} style={{
                    padding: '8px 14px', borderRadius: 20, cursor: 'pointer',
                    background: 'var(--bg-card)', border: '1px solid var(--border)',
                    color: 'var(--text-secondary)', fontSize: 14,
                  }}>{c}</button>
                ))}
              </div>
            )}
            <QuizContinue enabled={true} onClick={finish} label="Показать мой разбор →" />
          </div>
        </div>
      )}
    </div>
  );
}

/* ---- Экран 3: Персональный ответ (результат, структурный) ---- */
const SEGMENT_DATA = {
  novice: {
    title: 'Растите свой стройбизнес в Германии — с сообществом и инструментами клуба',
    bullets: [
      { Icon: MessageCircle, text: 'Сообщество и знания: как развивать стройбизнес в Германии' },
      { Icon: Scale,         text: 'Эксперты клуба — налоговые консультанты (Steuerberater), юристы (Rechtsanwalt), страховщики и др.' },
      { Icon: Home,          text: 'Прямые клиенты через платформу Fix-Hub.de — 0% комиссий' },
      { Icon: Receipt,       text: 'Инструмент для смет и счетов — за минуты, на русском' },
    ],
    result: 'Результат: вы выходите на прямых клиентов и растёте в кругу своих — без посредников и без догадок.',
  },
  subcontractor: {
    title: 'Выход из субподряда — это система, а не везение',
    bullets: [
      { Icon: Home,       text: 'Прямые клиенты через платформу Fix-Hub.de — 0% комиссий' },
      { Icon: Clipboard,  text: '1120+ объектов прошло через биржу клуба — обмен заказами между своими' },
      { Icon: TrendingUp, text: 'Меньше зависимости от генподрядчика — выше маржа' },
    ],
    result: 'Результат: вы меньше зависите от генподряда и зарабатываете больше на прямых заказах.',
  },
  firm: {
    title: 'Сеть проверенных — расти качеством, а не демпингом',
    bullets: [
      { Icon: Handshake, text: 'Надёжные мастера и субподрядчики — из круга своих' },
      { Icon: Link,      text: 'Партнёрства и обмен крупными объектами' },
      { Icon: Shield,    text: 'Биржа объектов и чёрный список — меньше рисков' },
    ],
    result: 'Результат: надёжная команда и партнёры, меньше рисков — рост качеством, а не демпингом.',
  },
};

const SEGMENT_FALLBACK = {
  title: 'Вот что даёт клуб:',
  bullets: [
    { Icon: Home,      text: 'Прямые клиенты — 0% комиссий' },
    { Icon: Clipboard, text: 'Биржа объектов' },
    { Icon: Scale,     text: 'Сметы, налоги, право' },
  ],
  result: 'Результат: всё для роста стройбизнеса в одном месте.',
};

function SegmentSlide({ animKey, segment, onNext }) {
  const data = (segment && SEGMENT_DATA[segment]) ? SEGMENT_DATA[segment] : SEGMENT_FALLBACK;

  return (
    <div style={{ width: '100%', maxWidth: 400 }}>
      <p key={`s1-${animKey}`} className="onb-anim-1" style={{
        fontSize: 13, fontWeight: 700, color: 'var(--accent)',
        textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8,
      }}>
        Ваш персональный разбор
      </p>

      <h2 key={`s2-${animKey}`} className="onb-anim-1" style={{
        fontSize: 22, fontWeight: 700, marginBottom: 6,
        color: 'var(--text-primary)', lineHeight: 1.3,
      }}>
        {data.title}
      </h2>
      <p key={`s2s-${animKey}`} className="onb-anim-2" style={{
        fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 20,
      }}>
        Вот что вы получите в клубе:
      </p>

      <div key={`s3-${animKey}`} style={{ marginBottom: 16 }}>
        {data.bullets.map((b, i) => (
          <div key={i} className={`icon-pop icon-pop-${i + 1}`} style={{
            display: 'flex', alignItems: 'flex-start', gap: 14,
            background: 'var(--bg-card)', borderRadius: 14, padding: '14px 16px',
            border: '1px solid var(--border)', marginBottom: 10,
          }}>
            <IconBadge accent={true} size={38} iconSize={20}>
              <b.Icon />
            </IconBadge>
            <span style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5, paddingTop: 8 }}>{b.text}</span>
          </div>
        ))}
      </div>

      {/* Персональный результат */}
      <div key={`sr-${animKey}`} className="onb-anim-3" style={{
        display: 'flex', alignItems: 'flex-start', gap: 10,
        background: 'var(--accent-dim)', borderRadius: 14, padding: '14px 16px',
        marginBottom: 20,
      }}>
        <span style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 1 }}>
          <TrendingUp size={18} />
        </span>
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.45 }}>
          {data.result}
        </span>
      </div>

      <div key={`s4-${animKey}`} className="onb-anim-4">
        <button onClick={onNext} style={{
          width: '100%', padding: '16px 0',
          background: 'var(--accent)', color: '#0D0D0D',
          border: 'none', borderRadius: 14, fontSize: 16, fontWeight: 700, cursor: 'pointer',
        }}>
          Как устроен клуб →
        </button>
      </div>
    </div>
  );
}

/* ---- Экран 4: Преимущества и инструменты клуба (сетка 2×4 квадратов) ---- */
const CLUB_BLOCKS = [
  { Icon: Users,         title: 'Сообщество 450+', text: 'строители по всей Германии',
    detail: 'Более 450 строительных фирм и бригад в Германии. Свой круг — делятся объектами, опытом, контактами, без посредников и случайных людей.' },
  { Icon: Scale,         title: 'Эксперты клуба', text: 'юристы, налоги, страхование',
    detail: 'Юристы, адвокаты, налоговые консультанты отвечают на ваши вопросы по работе в Германии: договоры, налоги, споры, документы.' },
  { Icon: BookOpen,      title: 'Вебинары', text: 'разборы с экспертами',
    detail: 'Разборы с экспертами: налоги, право, расценки, привлечение клиентов. Записи остаются в базе знаний.' },
  { Icon: Link,          title: 'Обмен объектами', text: 'заказы и субподряд между своими',
    detail: 'Более 50 объектов в месяц между участниками: передают друг другу объекты и субподряд. Перегружены или ищете работу — клуб закрывает оба случая.' },
  { Icon: Home,          title: 'Платформа Fix-Hub.de', text: 'прямые заказы, 0% комиссий',
    detail: 'Маркетплейс и каталог участников — заказчики находят вас напрямую. 0% комиссий.' },
  { Icon: MessageCircle, title: 'Приложение Fix-Club', text: 'весь клуб в Telegram',
    detail: 'Внутреннее приложение клуба прямо в Telegram — общение, объекты, уведомления и инструменты в одном месте.' },
  { Icon: Receipt,       title: 'Chat-Angebot.de', text: 'сметы и счета за минуты',
    detail: 'Сметы и счета (Angebote/Rechnungen) за минуты, с ценами по регионам Германии. Меньше бумажной рутины — больше работы.' },
  { Icon: Shield,        title: 'Проверенные люди', text: 'свой круг, чёрный список',
    detail: 'Свой круг — проверенные участники и чёрный список ненадёжных. Работаете с теми, за кого ручается клуб, — меньше рисков.' },
];

// Плитка-преимущество: вся карточка кликабельна, деталь — в bottom-sheet (без джанка сетки)
function ClubTile({ block, onOpen }) {
  return (
    <button onClick={onOpen} className="club-tile" style={{
      flex: '1 1 calc(50% - 5px)', minWidth: 'calc(50% - 5px)', boxSizing: 'border-box',
      background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: 16, padding: '14px 12px 13px', minHeight: 132,
      display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
      textAlign: 'left', cursor: 'pointer', position: 'relative',
    }}>
      {/* индикатор кликабельности */}
      <span style={{
        position: 'absolute', top: 10, right: 10,
        width: 20, height: 20, borderRadius: '50%',
        border: '1px solid var(--border)', color: 'var(--text-tertiary)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
          <path d="M6 1.5v9M1.5 6h9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        </svg>
      </span>

      <IconBadge accent={true} size={40} iconSize={22}>
        <block.Icon />
      </IconBadge>
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.2, margin: '10px 0 0' }}>
        {block.title}
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', lineHeight: 1.35, marginTop: 3 }}>
        {block.text}
      </div>
    </button>
  );
}

// Bottom-sheet с деталью. Портал в body — иначе transform контейнера слайдов ломает position:fixed.
function ClubSheet({ block, onClose }) {
  if (!block) return null;
  return createPortal(
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 10010,
      background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-end',
      animation: 'onb-fadeIn 0.2s ease both',
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: '100%', maxWidth: 480, margin: '0 auto',
        background: 'var(--bg-elevated)', borderRadius: '22px 22px 0 0',
        padding: '10px 22px calc(24px + var(--tg-safe-bottom, 0px))',
        animation: 'onb-sheet-up 0.3s cubic-bezier(0.25,0.46,0.45,0.94) both',
      }}>
        <div style={{ width: 38, height: 4, borderRadius: 2, background: 'var(--border)', margin: '0 auto 18px' }} />
        <IconBadge accent={true} size={54} iconSize={28}>
          <block.Icon />
        </IconBadge>
        <h3 style={{ fontSize: 19, fontWeight: 800, color: 'var(--text-primary)', margin: '14px 0 8px', lineHeight: 1.25 }}>
          {block.title}
        </h3>
        <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 20px' }}>
          {block.detail}
        </p>
        <button onClick={onClose} style={{
          width: '100%', padding: '14px 0',
          background: 'var(--accent)', color: '#0D0D0D', border: 'none',
          borderRadius: 14, fontSize: 15, fontWeight: 700, cursor: 'pointer',
        }}>
          Понятно
        </button>
      </div>
    </div>,
    document.body
  );
}

function HowSlide({ animKey, onNext }) {
  const [active, setActive] = useState(null);
  return (
    <div style={{ width: '100%', maxWidth: 400, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <h2 key={`h1-${animKey}`} className="onb-anim-1" style={{
        fontSize: 21, fontWeight: 800, marginBottom: 6, textAlign: 'center',
        color: 'var(--text-primary)', lineHeight: 1.3, flexShrink: 0,
      }}>
        Преимущества и инструменты клуба
      </h2>
      <p key={`h1s-${animKey}`} className="onb-anim-2" style={{
        fontSize: 13, color: 'var(--text-tertiary)', textAlign: 'center', marginBottom: 12, flexShrink: 0,
      }}>
        Всё, что вы получаете внутри — нажмите на блок
      </p>

      {/* Скроллится ТОЛЬКО сетка; шапка и кнопка закреплены */}
      <div key={`h2-${animKey}`} className="onb-anim-3" style={{
        flex: '1 1 auto', minHeight: 0, overflowY: 'auto',
        display: 'flex', flexWrap: 'wrap', gap: 10, alignContent: 'flex-start',
        paddingBottom: 6,
      }}>
        {CLUB_BLOCKS.map((b, i) => (
          <ClubTile key={i} block={b} onOpen={() => setActive(b)} />
        ))}
      </div>

      {/* Закреплённый снизу CTA — всегда виден (gradient-затухание над сеткой) */}
      <div style={{
        flexShrink: 0, position: 'relative', zIndex: 2,
        paddingTop: 14, marginTop: -6,
        background: 'linear-gradient(to top, var(--bg-primary) 72%, transparent)',
      }}>
        <button onClick={onNext} style={{
          width: '100%', padding: '16px 0',
          background: 'var(--accent)', color: '#0D0D0D',
          border: 'none', borderRadius: 14, fontSize: 16, fontWeight: 700, cursor: 'pointer',
          boxShadow: '0 6px 20px rgba(255, 204, 0, 0.28)',
        }}>
          Продолжить →
        </button>
      </div>

      <ClubSheet block={active} onClose={() => setActive(null)} />
    </div>
  );
}

/* ---- Экран 5: Развилка ---- */
function ForkSlide({ animKey, onPick, returning, onAlreadyKnown }) {
  const [ack, setAck] = useState(false);
  return (
    <div style={{ textAlign: 'center', width: '100%', maxWidth: 400, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      <h2 key={`f1-${animKey}`} className="onb-anim-1" style={{
        fontSize: 22, fontWeight: 800, marginBottom: 8,
        color: 'var(--text-primary)', lineHeight: 1.3,
      }}>
        Прежде чем вступить — познакомьтесь с клубом
      </h2>
      <p key={`f1s-${animKey}`} className="onb-anim-2" style={{
        fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 28,
      }}>
        Так вы поймёте, подходит ли клуб именно вам. Выберите формат:
      </p>

      <div key={`f2-${animKey}`} className="onb-anim-3">
        <button onClick={() => onPick('video')} style={{
          width: '100%', padding: '17px 16px',
          background: 'var(--accent)', color: '#0D0D0D',
          border: 'none', borderRadius: 14, fontSize: 16, fontWeight: 700, cursor: 'pointer',
          marginBottom: 14,
        }}>
          🎥 Смотреть короткую презентацию
        </button>

        <button onClick={() => onPick('excursion')} style={{
          width: '100%', padding: '14px 16px',
          background: 'var(--bg-card)', color: 'var(--text-primary)',
          border: '1.5px solid var(--border)', borderRadius: 14,
          cursor: 'pointer',
        }}>
          <span style={{ display: 'block', fontSize: 15, fontWeight: 700, lineHeight: 1.3 }}>
            👤 Живая экскурсия 1:1 с основателем
          </span>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-tertiary)', marginTop: 3 }}>
            30 минут · бесплатно
          </span>
        </button>
      </div>
      </div>

      {/* Возвратный: прижато к низу экрана — уже знаком, ставит галочку и сразу вступает */}
      {returning && (
        <div style={{ paddingTop: 18, borderTop: '1px solid var(--border)' }}>
          <GateCheckbox
            checked={ack}
            onToggle={() => setAck(a => !a)}
            label="Я уже ознакомлен с информацией"
          />
          <button
            onClick={() => ack && onAlreadyKnown()}
            disabled={!ack}
            style={{
              width: '100%', padding: '14px 0',
              background: ack ? 'var(--accent)' : 'var(--bg-elevated)',
              color: ack ? '#0A0A0A' : 'var(--text-tertiary)',
              border: 'none', borderRadius: 14, fontSize: 15, fontWeight: 700,
              cursor: ack ? 'pointer' : 'default', transition: 'all 0.2s',
            }}
          >
            Вступить в клуб →
          </button>
        </div>
      )}
    </div>
  );
}

/* ---- Анкета (обязательная, после развилки) ---- */
function FormSlide({ animKey, contact, setContact, path, onSubmit, demo = false }) {
  const { firstName, lastName, phone, email } = contact;
  const set = (patch) => setContact(c => ({ ...c, ...patch }));
  const [saving, setSaving] = useState(false);

  const phoneDigits = (phone || '').replace(/(?!^\+)[^\d]/g, '');
  const phoneOk = phoneDigits.length >= 7 && phoneDigits.length <= 20;
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || '').trim());
  const canSubmit = firstName.trim() && lastName.trim() && phoneOk && emailOk && !saving;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    if (!demo) {
      try { await sendProfile({ first_name: firstName, last_name: lastName, phone: phoneDigits, email: (email || '').trim(), path }); }
      catch (e) { /* не блокируем переход */ }
    }
    setSaving(false);
    if (!demo) sendFunnelEvent('onb_form_submit');
    onSubmit();
  };

  const inputStyle = {
    width: '100%', padding: '14px 16px',
    background: 'var(--bg-card)', color: 'var(--text-primary)',
    border: '1.5px solid var(--border)', borderRadius: 14,
    fontSize: 16, boxSizing: 'border-box', marginBottom: 10, outline: 'none',
  };

  return (
    <div style={{ width: '100%', maxWidth: 400 }}>
      <h2 key={`fm1-${animKey}`} className="onb-anim-1" style={{
        fontSize: 21, fontWeight: 800, marginBottom: 8, textAlign: 'center',
        color: 'var(--text-primary)', lineHeight: 1.3,
      }}>
        Оставьте контакты — и продолжим
      </h2>
      <p key={`fm2-${animKey}`} className="onb-anim-2" style={{
        fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5,
        marginBottom: 22, textAlign: 'center',
      }}>
        Сохраним ваш доступ и свяжемся, если будут вопросы.
      </p>

      <div key={`fm3-${animKey}`} className="onb-anim-3">
        <input type="text" value={firstName} onChange={(e) => set({ firstName: e.target.value })}
          onFocus={liftField} placeholder="Имя" maxLength={80} style={inputStyle} />
        <input type="text" value={lastName} onChange={(e) => set({ lastName: e.target.value })}
          onFocus={liftField} placeholder="Фамилия" maxLength={80} style={inputStyle} />

        <input type="tel" value={phone} onChange={(e) => set({ phone: e.target.value })}
          onFocus={liftField} placeholder="Телефон, например +49 …" style={inputStyle} />

        <input type="email" value={email} onChange={(e) => set({ email: e.target.value })}
          onFocus={liftField} placeholder="Email" maxLength={120} autoCapitalize="none" autoCorrect="off"
          inputMode="email" style={inputStyle} />

        <button onClick={handleSubmit} disabled={!canSubmit} style={{
          width: '100%', padding: '16px 0', marginTop: 6,
          background: canSubmit ? 'var(--accent)' : 'var(--bg-elevated)',
          color: canSubmit ? '#0D0D0D' : 'var(--text-tertiary)',
          border: 'none', borderRadius: 14, fontSize: 16, fontWeight: 700,
          cursor: canSubmit ? 'pointer' : 'default', transition: 'all 0.2s',
        }}>
          {saving ? 'Сохраняем…' : 'Продолжить →'}
        </button>

        <p style={{ fontSize: 12, color: 'var(--text-tertiary)', lineHeight: 1.5, marginTop: 12, textAlign: 'center' }}>
          Ваши данные не передаём третьим лицам и обрабатываем{' '}
          <span
            onClick={() => {
              const tg = window.Telegram?.WebApp;
              const url = 'https://fix-hub.de/datenschutz/';
              if (tg?.openLink) tg.openLink(url); else window.open(url, '_blank');
            }}
            style={{ color: 'var(--accent)', textDecoration: 'underline', cursor: 'pointer' }}
          >
            согласно политике конфиденциальности
          </span>.
        </p>
      </div>
    </div>
  );
}

/* ---- Чекбокс-гейт ---- */
function GateCheckbox({ checked, onToggle, label }) {
  return (
    <div onClick={onToggle} style={{
      display: 'flex', alignItems: 'center', gap: 11, cursor: 'pointer',
      padding: '14px 14px', textAlign: 'left',
      background: 'var(--bg-card)', border: `1.5px solid ${checked ? 'var(--accent)' : 'var(--border)'}`,
      borderRadius: 14, marginBottom: 14, transition: 'border-color 0.2s',
    }}>
      <span style={{
        width: 24, height: 24, borderRadius: 7, flexShrink: 0,
        border: `2px solid ${checked ? 'var(--accent)' : 'var(--border)'}`,
        background: checked ? 'var(--accent)' : 'transparent',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {checked && <Check size={15} style={{ color: '#0D0D0D' }} />}
      </span>
      <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.35 }}>{label}</span>
    </div>
  );
}

/* ---- Презентация ИЛИ Экскурсия (по выбору на развилке) + гейт-чекбокс ---- */
function PresentSlide({ animKey, path, onCall, onContinue }) {
  const [agreed, setAgreed] = useState(false);
  const isExcursion = path === 'excursion';
  const tgUser = window.Telegram?.WebApp?.initDataUnsafe?.user;
  const prefill = tgUser
    ? `?name=${encodeURIComponent((tgUser.first_name || '') + ' ' + (tgUser.last_name || ''))}`
    : '';

  const openLink = (url) => {
    const tg = window.Telegram?.WebApp;
    if (tg?.openLink) tg.openLink(url);
    else window.open(url, '_blank');
  };
  const [tourState, setTourState] = useState('idle'); // idle | sending | sent | error
  const openCalendly = async () => {
    if (TOUR_SELF_BOOKING) { openLink(CALENDLY_URL + prefill); onCall(); return; }
    if (tourState === 'sending' || tourState === 'sent') return;
    setTourState('sending');
    try {
      await requestTour();
      setTourState('sent');
      onCall();
    } catch (e) {
      setTourState('error');
    }
  };

  const continueBtn = (
    <button onClick={() => agreed && onContinue()} disabled={!agreed} style={{
      width: '100%', padding: '16px 0', marginTop: 4,
      background: agreed ? 'var(--accent)' : 'var(--bg-elevated)',
      color: agreed ? '#0D0D0D' : 'var(--text-tertiary)',
      border: 'none', borderRadius: 14, fontSize: 16, fontWeight: 700,
      cursor: agreed ? 'pointer' : 'default', transition: 'all 0.2s',
      boxShadow: agreed ? '0 6px 20px rgba(255,204,0,0.28)' : 'none',
    }}>
      Перейти к выбору пакета →
    </button>
  );

  if (isExcursion) {
    return (
      <div style={{ width: '100%', maxWidth: 400, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flexShrink: 0 }}>
          <h2 key={`pr1-${animKey}`} className="onb-anim-1" style={{
            fontSize: 22, fontWeight: 800, marginBottom: 8, textAlign: 'center',
            color: 'var(--text-primary)', lineHeight: 1.3,
          }}>
            Запишитесь на бесплатную экскурсию
          </h2>
          <p key={`pr2-${animKey}`} className="onb-anim-2" style={{
            fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.55, marginBottom: 0, textAlign: 'center',
          }}>
            30 минут с основателем: покажем клуб под вашу ситуацию, ответим на вопросы и поймём, чем будем полезны именно вам.
          </p>
        </div>

        {/* Главное действие — акцентная кнопка по центру */}
        <div className="onb-anim-3" style={{
          flex: '1 1 auto', display: 'flex', flexDirection: 'column', justifyContent: 'center',
        }}>
          {TOUR_SELF_BOOKING ? (
            <>
              <button onClick={openCalendly} style={{
                width: '100%', padding: '18px 0',
                background: 'var(--accent)', color: '#0D0D0D',
                border: 'none', borderRadius: 14, fontSize: 17, fontWeight: 800, cursor: 'pointer',
                boxShadow: '0 8px 24px rgba(255,204,0,0.32)',
              }}>
                📅 Выбрать удобное время
              </button>
              <p style={{ fontSize: 12.5, color: 'var(--text-tertiary)', textAlign: 'center', margin: '12px 0 0' }}>
                Откроется календарь — выберите удобный слот
              </p>
            </>
          ) : tourState === 'sent' ? (
            <>
              <div style={{
                width: '100%', padding: '18px 14px', boxSizing: 'border-box', textAlign: 'center',
                background: 'var(--accent-dim)', border: '1px solid var(--accent)',
                borderRadius: 14, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)',
              }}>
                ✅ Заявка отправлена
              </div>
              <p style={{ fontSize: 12.5, color: 'var(--text-tertiary)', textAlign: 'center', margin: '12px 0 0' }}>
                Менеджер напишет вам в Telegram и подберёт удобное время
              </p>
            </>
          ) : (
            <>
              <button onClick={openCalendly} disabled={tourState === 'sending'} style={{
                width: '100%', padding: '18px 0',
                background: 'var(--accent)', color: '#0D0D0D',
                border: 'none', borderRadius: 14, fontSize: 17, fontWeight: 800, cursor: 'pointer',
                boxShadow: '0 8px 24px rgba(255,204,0,0.32)',
                opacity: tourState === 'sending' ? 0.7 : 1,
              }}>
                {tourState === 'sending' ? 'Отправляем…' : '📅 Записаться на экскурсию'}
              </button>
              <p style={{ fontSize: 12.5, color: tourState === 'error' ? 'var(--error, #ff6b6b)' : 'var(--text-tertiary)', textAlign: 'center', margin: '12px 0 0' }}>
                {tourState === 'error'
                  ? 'Не получилось отправить — попробуйте ещё раз или напишите нам в бот'
                  : 'Менеджер свяжется с вами в Telegram и подберёт удобное время'}
              </p>
            </>
          )}
        </div>

        {/* Гейт — внизу, чтобы не отвлекал от записи */}
        <div style={{ flexShrink: 0 }}>
          <GateCheckbox checked={agreed} onToggle={() => setAgreed(a => !a)}
            label={TOUR_SELF_BOOKING ? "Я записался и ознакомился с информацией" : "Я оставил заявку и ознакомился с информацией"} />
          {continueBtn}
        </div>
      </div>
    );
  }

  // Видео-презентация
  return (
    <div style={{ width: '100%', maxWidth: 400 }}>
      <h2 key={`pv1-${animKey}`} className="onb-anim-1" style={{
        fontSize: 22, fontWeight: 800, marginBottom: 14, textAlign: 'center',
        color: 'var(--text-primary)', lineHeight: 1.3,
      }}>
        Короткая презентация клуба
      </h2>

      <div key={`pv2-${animKey}`} className="onb-anim-2" style={{
        padding: 3, borderRadius: 20,
        background: 'linear-gradient(135deg, var(--accent), #E6B800, var(--accent))',
        boxShadow: '0 0 30px rgba(255, 204, 0, 0.15)', marginBottom: 8,
      }}>
        <div style={{
          width: '100%', aspectRatio: '16/9', borderRadius: 17, overflow: 'hidden',
          background: 'var(--bg-card)', position: 'relative',
        }}>
          <iframe
            src="https://www.youtube.com/embed/Ubr0TEgKxCg?rel=0&modestbranding=1&playsinline=1"
            title="Fix-Hub — презентация"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
          />
        </div>
      </div>
      <p onClick={() => openLink('https://youtu.be/Ubr0TEgKxCg')} style={{
        fontSize: 12.5, color: 'var(--text-tertiary)', textAlign: 'center',
        marginBottom: 16, cursor: 'pointer', textDecoration: 'underline',
      }}>
        Открыть видео на YouTube
      </p>

      <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 16, textAlign: 'center' }}>
        В презентации — действующие бонусы и условия вступления. Они могут измениться — посмотрите, пока актуальны.
      </p>

      <GateCheckbox checked={agreed} onToggle={() => setAgreed(a => !a)}
        label="Я посмотрел и ознакомился с информацией" />
      {!agreed && (
        <p style={{ fontSize: 12.5, color: 'var(--text-tertiary)', textAlign: 'center', margin: '0 0 12px', lineHeight: 1.4 }}>
          Посмотрите презентацию — так участие будет осознанным.
        </p>
      )}
      {continueBtn}
    </div>
  );
}

// ---- Экран 14 (DEV): Пакеты — новая лестница start/standard/pro + промокод ----
// Цены и Stripe-ссылки НЕ захардкожены: приходят из настроек админки
// (GET /api/subscription/plans ← stats-ключи plan_link_* / plan_price_*).
// Логика завязана на коды планов (start/standard/pro), не на отображаемые названия.
const PLAN_DEFS = [
  {
    code: 'start',
    name: 'Fix-Club',
    tier: 'Start',
    bullets: [
      'Закрытое сообщество FIX-CLUB',
      'Обмен объектами внутри клуба',
      '2 вебинара в месяц',
      'Вопросы экспертам: налоги, право и др.',
    ],
    details: [
      'Закрытое сообщество FIX-CLUB (группа-форум)',
      'Обмен объектами внутри клуба',
      '2 вебинара в месяц',
      'Вопросы экспертам: налоги, право и другие категории',
      'Чёрный список заказчиков и отзывы',
      'События, новости, полезные материалы',
    ],
  },
  {
    code: 'standard',
    name: 'Fix-Hub + Platform',
    tier: 'Standard',
    includesNote: 'Всё из Fix-Club (Start), плюс:',
    bullets: [
      'Платформа прямых клиентов fix-hub.de',
      'Заявки от немецких заказчиков — 0% комиссий',
    ],
    details: [
      'Всё из пакета Fix-Club (Start)',
      'Платформа прямых клиентов fix-hub.de',
      'Заявки от немецких заказчиков — 0% комиссий',
    ],
  },
  {
    code: 'pro',
    name: 'Fix-Hub + Programm',
    tier: 'Pro',
    includesNote: 'Всё из Standard, плюс:',
    bullets: [
      'VIP-инструмент chat-angebot.de',
      '1 бесплатная консультация с организатором клуба',
      'Электронная визитка с QR-кодом',
    ],
    details: [
      'Всё из пакета Fix-Hub + Platform (Standard)',
      'VIP-инструмент chat-angebot.de',
      '1 бесплатная консультация с организатором клуба',
      'Электронная визитка с QR-кодом',
    ],
    bonusNote: 'Бонус для Pro — бесплатная электронная визитка',
    cardExample: true,
  },
];

export function PlansSlide({ animKey, onPayClick, active = true, showContactLine = true, demo = false, onDemoDone }) {
  const [period, setPeriod] = useState('month');
  const [plansCfg, setPlansCfg] = useState(null);
  const [selected, setSelected] = useState(null); // выбранный пакет (радио); единая кнопка «Оплатить» активируется после выбора
  const [promoCode, setPromoCode] = useState('');
  const [promoBusy, setPromoBusy] = useState(false);
  const [promoMsg, setPromoMsg] = useState(null);        // { ok, text }
  const [promoFocused, setPromoFocused] = useState(false); // фокус на промокоде — даём место под клавиатуру
  const promoRef = useRef(null);
  const promoBoxRef = useRef(null);

  // Флаг «онбординг пройден» — только когда слайд реально показан (см. коммент в проде)
  useEffect(() => {
    if (!active || demo) return;
    try { localStorage.setItem('fixhub_onboarding_done', '1'); } catch {}
  }, [active, demo]);

  // Конфиг пакетов с сервера (цены + ссылки из админки)
  useEffect(() => {
    getPlansConfig().then(d => setPlansCfg(d.plans || {})).catch(() => setPlansCfg({}));
  }, []);

  const cfgOf = (code) => (plansCfg && plansCfg[code]) || {};
  const priceLabel = (code) => {
    const c = cfgOf(code);
    const p = period === 'month' ? c.price_month : c.price_year;
    if (!p) return period === 'month' ? '€—' : 'цена за год — при оплате';
    return `€${p}`;
  };

  const openPayment = (code) => {
    const tg = window.Telegram?.WebApp;
    const tgId = tg?.initDataUnsafe?.user?.id;
    if (!tgId) {
      const msg = 'Оплата доступна только в приложении Telegram. Откройте, пожалуйста, клуб через Telegram-бота.';
      if (tg?.showAlert) tg.showAlert(msg); else alert(msg);
      return;
    }
    const link = period === 'month' ? cfgOf(code).link_month : cfgOf(code).link_year;
    if (!link) {
      const msg = 'Ссылка оплаты этого пакета появится совсем скоро. Напишите менеджеру — поможем.';
      if (tg?.showAlert) tg.showAlert(msg); else alert(msg);
      return;
    }
    if (onPayClick) onPayClick(code, period);
    const fullUrl = `${link}${link.includes('?') ? '&' : '?'}client_reference_id=${tgId}`;
    if (tg?.openLink) tg.openLink(fullUrl); else window.open(fullUrl, '_blank');
  };

  // Пример электронной визитки (бонус Pro). Ссылки пока нет — говорим об этом вместо
  // мёртвого клика. stopPropagation: клик не должен «проваливаться» в выбор карточки.
  const openCardExample = (e) => {
    e.stopPropagation();
    const tg = window.Telegram?.WebApp;
    if (!TODO_CARD_EXAMPLE_URL) {
      const msg = 'Пример визитки появится совсем скоро.';
      if (tg?.showAlert) tg.showAlert(msg); else alert(msg);
      return;
    }
    if (tg?.openLink) tg.openLink(TODO_CARD_EXAMPLE_URL); else window.open(TODO_CARD_EXAMPLE_URL, '_blank');
  };

  // Промокод: вся проверка на сервере. Успех → доступ уже выдан → перезагружаем аппку.
  const submitPromo = async () => {
    const code = promoCode.trim();
    if (!code || promoBusy) return;
    setPromoBusy(true);
    setPromoMsg(null);
    if (demo) {
      // Демо-показ: бэкенд не трогаем, «активируем» локально и возвращаем в аппку
      if (code.toUpperCase() === DEMO_PROMO_CODE) {
        setPromoMsg({ ok: true, text: 'Готово! Доступ открыт. Открываем приложение…' });
        setTimeout(() => { if (onDemoDone) onDemoDone(); }, 2000);
      } else {
        setPromoMsg({ ok: false, text: 'Такого промокода нет' });
      }
      setPromoBusy(false);
      return;
    }
    try {
      const r = await redeemPromoCode(code);
      const until = String(r.access_until || '').slice(0, 10).split('-').reverse().join('.');
      setPromoMsg({ ok: true, text: `Готово! Доступ открыт до ${until}. Открываем приложение…` });
      setTimeout(() => window.location.reload(), 2000);
    } catch (e) {
      setPromoMsg({ ok: false, text: e.message || 'Не получилось активировать код' });
    } finally {
      setPromoBusy(false);
    }
  };

  const openManager = () => {
    const tg = window.Telegram?.WebApp;
    const url = 'https://t.me/Ai_marketing_alex';
    if (tg?.openTelegramLink) tg.openTelegramLink(url); else window.open(url, '_blank');
  };

  return (
    <div style={{ width: '100%', maxWidth: 400, paddingBottom: promoFocused ? 120 : 0, transition: 'padding-bottom .2s ease' }}>
      <h2 key={`p1-${animKey}`} className="onb-anim-1" style={{
        fontSize: 24, fontWeight: 800, textAlign: 'center', marginBottom: 5, color: 'var(--text-primary)', letterSpacing: '-0.4px',
      }}>
        Выберите формат участия
      </h2>
      <p key={`p1s-${animKey}`} className="onb-anim-1" style={{
        fontSize: 12.5, textAlign: 'center', marginBottom: 16, color: 'var(--text-tertiary)', lineHeight: 1.4,
      }}>
        Доступ открывается сразу после оплаты · отмена в любой момент
      </p>

      {/* Переключатель месяц / год */}
      <div key={`p2-${animKey}`} className="onb-anim-2" style={{
        display: 'flex', background: 'var(--bg-card)', borderRadius: 12,
        padding: 4, marginBottom: 14, border: '1px solid var(--border)',
      }}>
        {[['month', 'Помесячно'], ['year', 'На год']].map(([val, label]) => (
          <button key={val} className="plan-toggle" onClick={() => setPeriod(val)} style={{
            flex: 1, padding: '10px 0', border: 'none', borderRadius: 10,
            fontSize: 14, fontWeight: 600, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            background: period === val ? 'var(--accent)' : 'transparent',
            color: period === val ? '#0D0D0D' : 'var(--text-tertiary)',
            transition: 'all 0.2s',
          }}>
            {label}
            {val === 'year' && (
              <span style={{
                fontSize: 10, fontWeight: 800, padding: '2px 6px', borderRadius: 6,
                background: period === 'year' ? '#0D0D0D' : 'var(--accent)',
                color: period === 'year' ? 'var(--accent)' : '#0D0D0D',
              }}>−20%</span>
            )}
          </button>
        ))}
      </div>

      {/* Карточки-радио: клиент выбирает пакет; цена — акцент, детали раскрываются у выбранного */}
      {PLAN_DEFS.map((plan, idx) => {
        const isSel = selected === plan.code;
        const list = plan.details || plan.bullets;
        const recommended = plan.code === 'standard';
        return (
          <div key={`pc-${plan.code}-${animKey}`} className={`onb-anim-${Math.min(idx + 3, 4)} plan-card`}
            onClick={() => setSelected(plan.code)}
            style={{
              background: isSel ? 'var(--bg-elevated)' : 'var(--bg-card)',
              borderRadius: 16, marginBottom: 10, overflow: 'hidden', cursor: 'pointer',
              border: isSel ? '2px solid var(--accent)' : '1.5px solid var(--border)',
              boxShadow: isSel ? '0 0 0 3px rgba(255,204,0,0.12), inset 0 1px 0 rgba(255,255,255,0.05)' : 'none',
              transition: 'border-color .2s ease, background .2s ease, box-shadow .2s ease',
              position: 'relative',
            }}>
            {recommended && (
              <div style={{
                position: 'absolute', top: 0, right: 0, background: 'var(--accent)', color: '#0D0D0D',
                fontSize: 9.5, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase',
                padding: '3px 9px', borderBottomLeftRadius: 10,
              }}>Хит</div>
            )}

            {/* Строка выбора: радио · название · ЦЕНА (герой) */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '15px 16px' }}>
              <span style={{
                flexShrink: 0, width: 22, height: 22, borderRadius: '50%',
                border: isSel ? '7px solid var(--accent)' : '2px solid var(--border)',
                transition: 'border .15s ease',
              }} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.25 }}>{plan.name}</div>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--accent)', marginTop: 2 }}>{plan.tier}</div>
              </div>
              <div key={`pr-${plan.code}-${period}`} className="price-pop" style={{ flexShrink: 0, textAlign: 'right' }}>
                <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--accent)', lineHeight: 1, whiteSpace: 'nowrap' }}>{priceLabel(plan.code)}</div>
                <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-tertiary)', marginTop: 3 }}>
                  {period === 'month' ? 'в месяц' : (cfgOf(plan.code).price_year ? 'в год' : '')}
                </div>
                {period === 'year' && cfgOf(plan.code).price_year && cfgOf(plan.code).price_month && (
                  <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--success, #34C759)', marginTop: 3, whiteSpace: 'nowrap' }}>
                    выгода €{Number(cfgOf(plan.code).price_month) * 12 - Number(cfgOf(plan.code).price_year)}
                  </div>
                )}
              </div>
            </div>

            {/* Детали выбранного пакета */}
            <div style={{
              maxHeight: isSel ? 460 : 0, opacity: isSel ? 1 : 0,
              transition: 'max-height .32s ease, opacity .25s ease', overflow: 'hidden',
            }}>
              <div key={isSel ? 'open' : 'closed'} style={{ padding: '0 16px 15px 50px' }}>
                {plan.includesNote && (
                  <div style={{ fontSize: 11.5, color: 'var(--accent)', fontWeight: 600, marginBottom: 6 }}>{plan.includesNote}</div>
                )}
                {list.map((b, i) => (
                  <div key={i} className="plan-reveal" style={{ display: 'flex', gap: 7, alignItems: 'flex-start', marginBottom: 6, animationDelay: `${0.04 + i * 0.05}s` }}>
                    <Check size={13} style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 2 }} />
                    <span style={{ fontSize: 12.5, lineHeight: 1.45, color: 'var(--text-secondary)' }}>{b}</span>
                  </div>
                ))}

                {plan.bonusNote && (
                  <div style={{
                    marginTop: 10, padding: '8px 10px', borderRadius: 10,
                    background: 'rgba(255,204,0,0.10)', border: '1px solid rgba(255,204,0,0.28)',
                    fontSize: 11.5, fontWeight: 700, lineHeight: 1.35, color: 'var(--accent)',
                  }}>{plan.bonusNote}</div>
                )}

                {plan.cardExample && (
                  <button type="button" onClick={openCardExample} style={{
                    marginTop: 8, width: '100%', padding: '10px 0', borderRadius: 10, cursor: 'pointer',
                    background: 'transparent', border: '1.5px solid var(--accent)',
                    color: 'var(--accent)', fontSize: 12.5, fontWeight: 700,
                  }}>Посмотреть пример визитки →</button>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {/* Единая кнопка оплаты — подсвечивается после выбора пакета */}
      <button
        onClick={() => selected && openPayment(selected)}
        disabled={!selected}
        className={selected ? 'plan-cta plan-pay-live' : ''}
        style={{
          width: '100%', padding: '16px 0', marginTop: 4, marginBottom: 10, borderRadius: 14, border: 'none',
          background: selected ? 'var(--accent)' : 'var(--bg-elevated)',
          color: selected ? '#0D0D0D' : 'var(--text-tertiary)',
          fontSize: 16, fontWeight: 800, cursor: selected ? 'pointer' : 'default',
          transition: 'background .25s ease, color .25s ease, box-shadow .25s ease',
          boxShadow: selected ? '0 8px 24px rgba(255,204,0,0.32)' : 'none',
        }}
      >
        {selected ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
            Оплатить {priceLabel(selected)}
            <span style={{
              width: 26, height: 26, borderRadius: '50%', background: 'rgba(0,0,0,0.15)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 700,
            }}>→</span>
          </span>
        ) : 'Выберите пакет ↑'}
      </button>

      {/* Промокод — всегда развёрнут */}
      <div key={`promo-${animKey}`} style={{ marginTop: 4, marginBottom: 8 }}>
        <div ref={promoBoxRef} style={{
          background: 'var(--bg-card)', border: '1px dashed var(--accent)', borderRadius: 14, padding: '14px 16px',
          scrollMarginTop: 12,
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>Есть промокод?</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              ref={promoRef}
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
              onFocus={(e) => { setPromoFocused(true); liftField(e); }}
              onBlur={() => setPromoFocused(false)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitPromo(); } }}
              placeholder="ПРОМОКОД"
              autoCapitalize="characters"
              autoCorrect="off"
              enterKeyHint="send"
              style={{
                flex: 1, minWidth: 0, padding: '11px 12px', borderRadius: 10,
                border: '1px solid var(--border)', background: 'var(--bg-elevated)',
                color: 'var(--text-primary)', fontSize: 16, letterSpacing: 1, outline: 'none',
              }}
            />
            <button onClick={submitPromo} className="plan-cta" disabled={promoBusy || !promoCode.trim()} style={{
              padding: '11px 16px', borderRadius: 10, border: 'none',
              background: promoBusy || !promoCode.trim() ? 'var(--bg-elevated)' : 'var(--accent)',
              color: promoBusy || !promoCode.trim() ? 'var(--text-tertiary)' : '#0D0D0D',
              fontSize: 13.5, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
            }}>
              {promoBusy ? '…' : 'Активировать'}
            </button>
          </div>
          {promoMsg && (
            <div style={{
              marginTop: 10, fontSize: 12.5, lineHeight: 1.45, fontWeight: 500,
              color: promoMsg.ok ? 'var(--success)' : 'var(--error, #FF453A)',
            }}>
              {promoMsg.text}
            </div>
          )}
        </div>
      </div>

      {showContactLine && (
        <div style={{ textAlign: 'center', marginTop: 6, marginBottom: 4 }}>
          <span onClick={openManager} style={{
            fontSize: 12.5, color: 'var(--text-tertiary)', textDecoration: 'underline', cursor: 'pointer',
          }}>
            Остались вопросы? Напишите нам
          </span>
        </div>
      )}

    </div>
  );
}

/* ---- Legacy: Video slide (v1) ---- */
function VideoSlide({ animKey }) {
  return (
    <div style={{ textAlign: 'center', width: '100%', maxWidth: 400 }}>
      <div key={`v1-${animKey}`} className="onb-anim-1">
        <img src="/logo.png" alt="Fix-Hub" style={{ height: 48, marginBottom: 20, objectFit: 'contain' }} />
      </div>
      <div key={`v2-${animKey}`} className="onb-anim-2" style={{
        padding: 3, borderRadius: 20,
        background: 'linear-gradient(135deg, var(--accent), #E6B800, var(--accent))',
        boxShadow: '0 0 30px rgba(255, 204, 0, 0.15)',
        marginBottom: 24,
      }}>
        <div style={{
          width: '100%', aspectRatio: '16/9', borderRadius: 17, overflow: 'hidden',
          background: 'var(--bg-card)', position: 'relative',
        }}>
          <iframe
            src="https://www.youtube.com/embed/oy2MzPmpk9I?rel=0&modestbranding=1&playsinline=1"
            title="Fix-Hub"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
          />
        </div>
      </div>
      <h2 key={`v3-${animKey}`} className="onb-anim-3" style={{ fontSize: 24, fontWeight: 700, marginBottom: 8, color: 'var(--text-primary)' }}>
        Добро пожаловать в Fix-Hub!
      </h2>
      <p key={`v4-${animKey}`} className="onb-anim-4" style={{ fontSize: 15, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
        Посмотри короткое видео о том, как работает клуб
      </p>
    </div>
  );
}

/* ---- Legacy: Excursion slide (v1) ---- */
function ExcursionSlide({ animKey }) {
  const tgUser = window.Telegram?.WebApp?.initDataUnsafe?.user;
  const prefill = tgUser
    ? `?name=${encodeURIComponent((tgUser.first_name || '') + ' ' + (tgUser.last_name || ''))}`
    : '';

  const [tourSent, setTourSent] = useState(false);
  const openCalendly = () => {
    if (TOUR_SELF_BOOKING) {
      const tg = window.Telegram?.WebApp;
      const url = CALENDLY_URL + prefill;
      if (tg?.openLink) tg.openLink(url);
      else window.open(url, '_blank');
      return;
    }
    if (tourSent) return;
    requestTour().then(() => setTourSent(true)).catch(() => {});
  };

  return (
    <div style={{ textAlign: 'center', width: '100%', maxWidth: 400 }}>
      <div key={`e1-${animKey}`} className="onb-anim-1" style={{
        width: 100, height: 100, borderRadius: '50%',
        background: 'var(--accent-dim)', margin: '0 auto 24px',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 48,
      }}>
        📞
      </div>
      <h2 key={`e2-${animKey}`} className="onb-anim-2" style={{ fontSize: 24, fontWeight: 700, marginBottom: 12, color: 'var(--text-primary)' }}>
        Персональная экскурсия
      </h2>
      <p key={`e3-${animKey}`} className="onb-anim-3" style={{ fontSize: 15, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 8 }}>
        Запишитесь на личный звонок-консультацию. Мы покажем все возможности клуба и ответим на вопросы.
      </p>
      <div key={`e4-${animKey}`} className="onb-anim-4" style={{
        background: 'var(--bg-card)', borderRadius: 16, padding: '20px',
        marginTop: 20, border: '1px solid var(--border)',
      }}>
        <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16, lineHeight: 1.5 }}>
          Выберите удобное время для звонка — мы проведём персональную экскурсию по всем возможностям клуба
        </div>
        <button onClick={openCalendly} style={{
          width: '100%', padding: '14px 0',
          background: 'var(--accent)', color: '#0D0D0D',
          border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer',
        }}>
          {TOUR_SELF_BOOKING ? 'Выбрать время' : (tourSent ? '✅ Заявка отправлена' : 'Записаться на экскурсию')}
        </button>
      </div>
    </div>
  );
}
